import {useCallback, useMemo, useState} from "react";
import {
    EBirdChecklistByFamily,
    EBirdMyDataSchema,
    EBirdObservationsBySpecies,
    EBirdSightingsCalibrationResult,
    getCalibratedObservations,
    getFilteredObservations,
    getMonthsWithObservations
} from "ebird-mydata-reader";

function getMonthsFromObservations(observations: EBirdMyDataSchema[]) {
    const monthSet = new Set<number>();

    for (const observation of observations) {
        const month = observation.observedMonth ?? parseInt(observation.date?.split('-')[1] ?? '', 10);
        if (Number.isFinite(month)) {
            monthSet.add(month);
        }
    }

    return Array.from(monthSet).sort((left, right) => left - right);
}

function filterCalibratedObservationsByMonth(observations: EBirdMyDataSchema[], month?: number) {
    if (!month || !Number.isFinite(month)) return observations;

    return observations.filter(observation => {
        const observedMonth = observation.observedMonth ?? parseInt(observation.date?.split('-')[1] ?? '', 10);
        return observedMonth === month;
    });
}

export function usePeriodFilter(
    rawData: EBirdMyDataSchema[],
    observationsBySpecies: EBirdObservationsBySpecies[],
    checklist: EBirdChecklistByFamily[] = [],
    calibrationResult?: EBirdSightingsCalibrationResult
) {
    const [year, setYear] = useState<number|string>();
    const [month, setMonth] = useState<number|string>();
    const [filteredObservations, setFilteredObservations] = useState<EBirdMyDataSchema[]>([]);

    const months = useMemo(() => {
        if (!year) {
            return [];
        }

        const parsedYear = year === 'life' ? year : parseInt(year as string);
        if (calibrationResult) {
            return getMonthsFromObservations(getCalibratedObservations(calibrationResult, parsedYear));
        }

        return getMonthsWithObservations(rawData, parsedYear);
    }, [calibrationResult, rawData, year]);

    const setTimePeriod = useCallback((year: string, month: string) => {
        const parsedYear = year === 'life' ? year : parseInt(year);
        const parsedMonth = parseInt(month);
        setYear(year);
        setMonth(month);
        if (calibrationResult) {
            setFilteredObservations(filterCalibratedObservationsByMonth(
                getCalibratedObservations(calibrationResult, parsedYear),
                parsedMonth
            ));
            return;
        }

        setFilteredObservations(getFilteredObservations(rawData, parsedYear, parsedMonth));
    }, [calibrationResult, rawData]);

    const setSpeciesFilter = useCallback((taxonomicOrder: number) => {
        const checklistSpecies = checklist
            .flatMap(family => family.speciesList)
            .find(species => {
                return species.taxonomicOrder === taxonomicOrder ||
                    species.alternateTaxonomicOrders?.includes(taxonomicOrder);
            });
        const taxonomicOrders = new Set<number>([
            checklistSpecies?.taxonomicOrder ?? taxonomicOrder,
            ...(checklistSpecies?.alternateTaxonomicOrders ?? [])
        ]);
        const matchingSpeciesGroups = observationsBySpecies.filter(el => taxonomicOrders.has(el.taxonomicOrder));
        const foundSpecies = matchingSpeciesGroups[0];

        if (foundSpecies) {
            setFilteredObservations(matchingSpeciesGroups.flatMap(species => species.observations));
        }

        return foundSpecies;
    }, [checklist, observationsBySpecies]);

    const resetPeriodFilter = useCallback(() => {
        setYear(undefined);
        setMonth(undefined);
        setFilteredObservations([]);
    }, []);

    return {
        year,
        month,
        months,
        filteredObservations,
        setYear,
        setMonth,
        setTimePeriod,
        setSpeciesFilter,
        resetPeriodFilter
    }
}
